import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit,
  Trash2,
  Package,
  Link as LinkIcon,
  RefreshCw,
  ExternalLink,
  Eye,
  MousePointer,
} from "lucide-react";
import { toast } from "sonner";
import CloudinaryUpload from "@/components/CloudinaryUpload";

const AdminBrands = () => {
  const { getAuthHeaders } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandDialogOpen, setBrandDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandProducts, setBrandProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [editingBrandId, setEditingBrandId] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkEdits, setBulkEdits] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [refreshingLogos, setRefreshingLogos] = useState(false);
  
  const [brandForm, setBrandForm] = useState({
    name: "",
    logo_url: "",
    description: "",
    commission_percent: "",
    cashback_percent: "",
    category: "Fashion",
    website_url: "",
  });

  const [productForm, setProductForm] = useState({
    product_url: "",
    title: "",
    description: "",
    image_url: "",
    original_price: "",
    discounted_price: "",
    category: "",
    override_commission: "",
    override_cashback: "",
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API}/admin/brands`, { headers: getAuthHeaders() });
      setBrands(res.data.brands || []);
    } catch (error) {
      toast.error("Failed to load brands");
    }
    setLoading(false);
  };

  const fetchBrandProducts = async (brandId) => {
    try {
      const res = await axios.get(`${API}/brands/${brandId}/products`, { headers: getAuthHeaders() });
      setBrandProducts(res.data.products || []);
    } catch (error) {
      toast.error("Failed to load products");
    }
  };

  const resetBrandForm = () => {
    setBrandForm({
      name: "",
      logo_url: "",
      description: "",
      commission_percent: "",
      cashback_percent: "",
      category: "Fashion",
      website_url: "",
    });
  };

  const resetProductForm = () => {
    setProductForm({
      product_url: "",
      title: "",
      description: "",
      image_url: "",
      original_price: "",
      discounted_price: "",
      override_commission: "",
      override_cashback: "",
    });
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...brandForm,
        commission_percent: parseFloat(brandForm.commission_percent),
        cashback_percent: parseFloat(brandForm.cashback_percent),
      };

      if (editingBrandId) {
        await axios.put(`${API}/admin/brands/${editingBrandId}`, payload, { headers: getAuthHeaders() });
        toast.success("Brand updated successfully!");
      } else {
        await axios.post(`${API}/admin/brands`, payload, { headers: getAuthHeaders() });
        toast.success("Brand created successfully!");
      }
      setBrandDialogOpen(false);
      setEditingBrandId(null);
      resetBrandForm();
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save brand");
    }
    setSubmitting(false);
  };

  const handleEditBrand = (brand) => {
    setEditingBrandId(brand.id);
    setBrandForm({
      name: brand.name || "",
      logo_url: brand.logo_url || "",
      description: brand.description || "",
      commission_percent: brand.commission_percent?.toString() || "",
      cashback_percent: brand.cashback_percent?.toString() || "",
      category: brand.category || "Fashion",
      website_url: brand.website_url || "",
    });
    setBrandDialogOpen(true);
  };

  const handleBrandDialogChange = (open) => {
    setBrandDialogOpen(open);
    if (!open) {
      setEditingBrandId(null);
      resetBrandForm();
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!confirm("Delete this brand and all its products?")) return;

    try {
      await axios.delete(`${API}/admin/brands/${brandId}`, { headers: getAuthHeaders() });
      toast.success("Brand deleted");
      fetchBrands();
    } catch (error) {
      toast.error("Failed to delete brand");
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!selectedBrand) return;
    setSubmitting(true);

    try {
      const payload = {
        brand_id: selectedBrand.id,
        product_url: productForm.product_url,
        title: productForm.title || null,
        description: productForm.description || null,
        image_url: productForm.image_url || null,
        original_price: productForm.original_price ? parseFloat(productForm.original_price) : null,
        discounted_price: productForm.discounted_price ? parseFloat(productForm.discounted_price) : null,
        category: productForm.category || null,
        override_commission: productForm.override_commission ? parseFloat(productForm.override_commission) : null,
        override_cashback: productForm.override_cashback ? parseFloat(productForm.override_cashback) : null,
      };

      const res = await axios.post(
        `${API}/admin/brands/${selectedBrand.id}/products`,
        payload,
        { headers: getAuthHeaders() }
      );
      
      toast.success(res.data.scraped ? "Product added with scraped details!" : "Product added (scraping may have failed)");
      setProductDialogOpen(false);
      resetProductForm();
      fetchBrandProducts(selectedBrand.id);
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add product");
    }
    setSubmitting(false);
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm("Delete this product?")) return;

    try {
      await axios.delete(`${API}/admin/brands/products/${productId}`, { headers: getAuthHeaders() });
      toast.success("Product deleted");
      fetchBrandProducts(selectedBrand.id);
      fetchBrands();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleRescrape = async (productId) => {
    setScraping(true);
    try {
      const res = await axios.post(
        `${API}/admin/brands/products/${productId}/rescrape`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        toast.success("Product details updated!");
        fetchBrandProducts(selectedBrand.id);
      } else {
        toast.error("Scraping failed - site may be blocking requests");
      }
    } catch (error) {
      toast.error("Failed to rescrape");
    }
    setScraping(false);
  };

  // ---------- Bulk Edit ----------
  const handleRefreshLogos = async () => {
    if (!confirm("This will overwrite existing brand logos with Clearbit auto-fetched logos. Continue?")) return;
    setRefreshingLogos(true);
    try {
      const res = await axios.post(
        `${API}/admin/brands/fix-logos`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(
        `Refreshed ${res.data.updated} / ${res.data.total} brand logos (${res.data.skipped} unchanged)`
      );
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to refresh logos");
    }
    setRefreshingLogos(false);
  };

  const enterBulkMode = () => {    const initial = {};
    brands.forEach((b) => {
      initial[b.id] = { logo_url: b.logo_url || "", website_url: b.website_url || "" };
    });
    setBulkEdits(initial);
    setBulkMode(true);
  };

  const cancelBulkMode = () => {
    setBulkMode(false);
    setBulkEdits({});
  };

  const updateBulkField = (brandId, field, value) => {
    setBulkEdits((prev) => ({
      ...prev,
      [brandId]: { ...prev[brandId], [field]: value },
    }));
  };

  const saveBulkChanges = async () => {    // Determine which brands actually changed
    const changes = brands.filter((b) => {
      const e = bulkEdits[b.id] || {};
      return (
        (e.logo_url || "") !== (b.logo_url || "") ||
        (e.website_url || "") !== (b.website_url || "")
      );
    });
    if (changes.length === 0) {
      toast.info("No changes to save");
      return;
    }
    setBulkSaving(true);
    let ok = 0;
    let fail = 0;
    for (const b of changes) {
      try {
        const payload = {
          logo_url: bulkEdits[b.id].logo_url || null,
          website_url: bulkEdits[b.id].website_url || null,
        };
        await axios.put(`${API}/admin/brands/${b.id}`, payload, {
          headers: getAuthHeaders(),
        });
        ok += 1;
      } catch (err) {
        fail += 1;
      }
    }
    setBulkSaving(false);
    if (fail === 0) {
      toast.success(`Updated ${ok} brand${ok !== 1 ? "s" : ""}`);
    } else {
      toast.error(`Saved ${ok}, failed ${fail}`);
    }
    setBulkMode(false);
    setBulkEdits({});
    fetchBrands();
  };

  const categories = ["Fashion", "Electronics", "Beauty", "Health", "Home", "Travel", "Food", "Finance", "Other"];

  return (
    <AdminLayout title="Manage Brands">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <p className="text-gray-500">Add brands and their affiliate products</p>
        <div className="flex items-center gap-2 flex-wrap">
          {!bulkMode ? (
            <>
              <Button
                variant="outline"
                onClick={handleRefreshLogos}
                disabled={loading || brands.length === 0 || refreshingLogos}
                data-testid="refresh-brand-logos-btn"
                title="Auto-fetch brand logos from their websites"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingLogos ? "animate-spin" : ""}`} />
                {refreshingLogos ? "Refreshing..." : "Refresh Logos"}
              </Button>
              <Button
                variant="outline"
                onClick={enterBulkMode}
                disabled={loading || brands.length === 0}
                data-testid="bulk-edit-brands-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Bulk Edit Logos & URLs
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={cancelBulkMode}
                disabled={bulkSaving}
                data-testid="bulk-edit-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={saveBulkChanges}
                disabled={bulkSaving}
                className="bg-primary-500 hover:bg-primary-600"
                data-testid="bulk-edit-save-btn"
              >
                {bulkSaving ? "Saving..." : "Save All Changes"}
              </Button>
            </>
          )}
          <Dialog open={brandDialogOpen} onOpenChange={handleBrandDialogChange}>
          <DialogTrigger asChild>
            <Button className="bg-primary-500 hover:bg-primary-600" data-testid="add-brand-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Brand
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingBrandId ? "Edit Brand" : "Add New Brand"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateBrand} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand Name *</Label>
                  <Input
                    value={brandForm.name}
                    onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
                    placeholder="e.g., Ajio"
                    required
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={brandForm.category} onValueChange={(v) => setBrandForm({ ...brandForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Logo</Label>
                <CloudinaryUpload
                  value={brandForm.logo_url}
                  onChange={(url) => setBrandForm({ ...brandForm, logo_url: url })}
                  folder="brands/"
                  resourceType="image"
                  testId="brand-logo-upload"
                />
              </div>

              <div>
                <Label>Website URL</Label>
                <Input
                  value={brandForm.website_url}
                  onChange={(e) => setBrandForm({ ...brandForm, website_url: e.target.value })}
                  placeholder="https://www.brand.com"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={brandForm.description}
                  onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })}
                  placeholder="Brief description of the brand..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Commission % *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={brandForm.commission_percent}
                    onChange={(e) => setBrandForm({ ...brandForm, commission_percent: e.target.value })}
                    placeholder="e.g., 10"
                    required
                  />
                </div>
                <div>
                  <Label>Cashback % *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={brandForm.cashback_percent}
                    onChange={(e) => setBrandForm({ ...brandForm, cashback_percent: e.target.value })}
                    placeholder="e.g., 5"
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting
                  ? (editingBrandId ? "Saving..." : "Creating...")
                  : (editingBrandId ? "Save Changes" : "Create Brand")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Bulk Edit Table */}
      {bulkMode && (
        <Card className="border-0 shadow-sm mb-6">
          <CardContent className="p-0">
            <div className="p-4 border-b bg-primary-50 text-sm text-primary-800">
              Edit all brand logos and website URLs at once. Click <strong>Save All Changes</strong> above when done.
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Logo</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Logo URL</TableHead>
                  <TableHead>Website URL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brands.map((brand) => {
                  const edit = bulkEdits[brand.id] || {};
                  return (
                    <TableRow key={brand.id}>
                      <TableCell>
                        <div className="w-10 h-10 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                          {edit.logo_url ? (
                            <img
                              src={edit.logo_url}
                              alt={brand.name}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <span className="text-xs font-bold text-gray-400">
                              {brand.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{brand.name}</TableCell>
                      <TableCell>
                        <Input
                          value={edit.logo_url || ""}
                          onChange={(e) => updateBulkField(brand.id, "logo_url", e.target.value)}
                          placeholder="https://logo.clearbit.com/brand.com"
                          className="text-xs"
                          data-testid={`bulk-logo-input-${brand.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={edit.website_url || ""}
                          onChange={(e) => updateBulkField(brand.id, "website_url", e.target.value)}
                          placeholder="https://www.brand.com"
                          className="text-xs"
                          data-testid={`bulk-website-input-${brand.id}`}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Brands Table */}
      {!bulkMode && loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : !bulkMode && brands.length > 0 ? (
        <Card className="border-0 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Brand</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-center">Commission</TableHead>
                <TableHead className="text-center">Cashback</TableHead>
                <TableHead className="text-center">Products</TableHead>
                <TableHead className="text-center">Total Clicks</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {brands.map((brand, idx) => {
                const gradients = [
                  "from-blue-500 to-indigo-600",
                  "from-purple-500 to-pink-500",
                  "from-fuchsia-500 to-pink-500",
                  "from-amber-500 to-red-500",
                  "from-cyan-500 to-blue-500",
                  "from-rose-500 to-pink-500",
                  "from-violet-500 to-purple-600",
                  "from-amber-500 to-amber-500",
                ];
                const gradient = gradients[idx % gradients.length];
                
                return (
                  <TableRow key={brand.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded flex items-center justify-center`}>
                          <span className="font-bold text-white text-sm">
                            {brand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{brand.name}</p>
                          {brand.website_url && (
                            <a href={brand.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-primary-500">
                              View Website
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  <TableCell>
                    <Badge variant="outline">{brand.category}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-green-100 text-green-700">{brand.commission_percent}%</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-primary-100 text-primary-700">{brand.cashback_percent}%</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium">{brand.product_count || 0}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium text-blue-600">{brand.total_clicks || 0}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBrand(brand);
                          fetchBrandProducts(brand.id);
                        }}
                      >
                        <Package className="w-4 h-4 mr-1" />
                        Products
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-500 hover:text-blue-700"
                        onClick={() => handleEditBrand(brand)}
                        data-testid={`edit-brand-${brand.id}`}
                        title="Edit brand & set website URL"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteBrand(brand.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
        </Card>
      ) : !bulkMode ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Brands Yet</h3>
            <p className="text-gray-500 mb-4">Add your first brand to start managing products</p>
            <Button onClick={() => setBrandDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Brand
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* Brand Products Sidebar/Modal */}
      {selectedBrand && (
        <Dialog open={!!selectedBrand} onOpenChange={() => setSelectedBrand(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedBrand.logo_url ? (
                  <img src={selectedBrand.logo_url} alt={selectedBrand.name} className="w-8 h-8 object-contain" />
                ) : (
                  <div className="w-8 h-8 bg-primary-100 rounded flex items-center justify-center">
                    <span className="font-bold text-primary-600">{selectedBrand.name.charAt(0)}</span>
                  </div>
                )}
                {selectedBrand.name} - Products
              </DialogTitle>
            </DialogHeader>

            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">{brandProducts.length} products</p>
              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Add Product by URL
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Product from URL</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddProduct} className="space-y-4">
                    <div>
                      <Label>Product URL *</Label>
                      <Input
                        value={productForm.product_url}
                        onChange={(e) => setProductForm({ ...productForm, product_url: e.target.value })}
                        placeholder="https://www.brand.com/product/..."
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        We'll try to auto-fetch product details from this URL
                      </p>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">Optional: Override scraped details</p>
                      
                      <div>
                        <Label>Product Title</Label>
                        <Input
                          value={productForm.title}
                          onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                          placeholder="Leave empty to auto-fetch"
                        />
                      </div>

                      <div className="mt-3">
                        <Label>Product Image</Label>
                        <CloudinaryUpload
                          value={productForm.image_url}
                          onChange={(url) => setProductForm({ ...productForm, image_url: url })}
                          folder="products/"
                          resourceType="image"
                          testId="brand-product-image-upload"
                        />
                      </div>

                      <div className="mt-3">
                        <Label>Category (Optional)</Label>
                        <Input
                          value={productForm.category}
                          onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                          placeholder="e.g. Fashion, Electronics, Books"
                          data-testid="brand-product-category-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <Label>Original Price</Label>
                          <Input
                            type="number"
                            value={productForm.original_price}
                            onChange={(e) => setProductForm({ ...productForm, original_price: e.target.value })}
                            placeholder="₹"
                          />
                        </div>
                        <div>
                          <Label>Discounted Price</Label>
                          <Input
                            type="number"
                            value={productForm.discounted_price}
                            onChange={(e) => setProductForm({ ...productForm, discounted_price: e.target.value })}
                            placeholder="₹"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <Label>Override Commission %</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={productForm.override_commission}
                            onChange={(e) => setProductForm({ ...productForm, override_commission: e.target.value })}
                            placeholder={`Default: ${selectedBrand.commission_percent}%`}
                          />
                        </div>
                        <div>
                          <Label>Override Cashback %</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={productForm.override_cashback}
                            onChange={(e) => setProductForm({ ...productForm, override_cashback: e.target.value })}
                            placeholder={`Default: ${selectedBrand.cashback_percent}%`}
                          />
                        </div>
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? "Adding..." : "Add Product"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Products Grid */}
            {brandProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {brandProducts.map((product) => (
                  <Card key={product.id} className="border overflow-hidden group">
                    <div className="relative aspect-square bg-gray-100">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button
                          onClick={() => handleRescrape(product.id)}
                          disabled={scraping}
                          className="p-1.5 bg-white rounded-full shadow hover:bg-gray-100"
                          title="Rescrape details"
                        >
                          <RefreshCw className={`w-4 h-4 text-gray-600 ${scraping ? 'animate-spin' : ''}`} />
                        </button>
                        <a
                          href={product.product_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 bg-white rounded-full shadow hover:bg-gray-100"
                        >
                          <ExternalLink className="w-4 h-4 text-gray-600" />
                        </a>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-1.5 bg-white rounded-full shadow hover:bg-red-100"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <Badge className="bg-black/70 text-white text-xs">
                          <MousePointer className="w-3 h-3 mr-1" />
                          {product.click_count || 0} clicks
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm font-medium line-clamp-2">{product.title}</p>
                      {product.discounted_price && (
                        <p className="text-sm font-bold text-gray-900 mt-1">
                          ₹{product.discounted_price.toLocaleString()}
                        </p>
                      )}
                      <div className="flex gap-1 mt-2">
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {product.commission_percent}% comm
                        </Badge>
                        <Badge className="bg-primary-100 text-primary-700 text-xs">
                          {product.cashback_percent}% cash
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <LinkIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No products yet. Add products by URL.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </AdminLayout>
  );
};

export default AdminBrands;
